// Contracts — kids on-site intake (2026-06-29). Self-service walk-in via a per-
// office (+optional moderator) link: a parent opens #/intake?token=… on site,
// fills parent + children, and is created as a checked_in meeting immediately (no
// booking/time). For Remote offices without a reception desk. Token mechanism =
// intake_links table (migration 0002). See spec "Apply / Intake" Режим 2.
import { z } from 'zod';
import { KidsGenderSchema, KidsEthnicitySchema } from '../kids';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Shared child shape for the two public data-only forms (intake + remote
// landing). Parents now supply most appearance data up front (Tim 2026-07-04)
// so the moderator's shot form only needs skin tone + lux. All appearance
// fields are optional/defaulted → old clients (name/age/gender only) stay valid.
//   ethnicity           — one of the 12 canonical values (KidsEthnicitySchema).
//   hasBeard/…/glasses  — boolean flags, default false (stored as INTEGER 0/1).
//   accessoryName       — free text (e.g. "hat", "earrings"), optional.
export const KidsPublicIntakeChildSchema = z.object({
  name: z.string().trim().min(1).max(100),
  age: z.number().int().min(5).max(17).optional(),
  gender: KidsGenderSchema.optional(),
  ethnicity: KidsEthnicitySchema.optional(),
  hasBeard: z.boolean().optional().default(false),
  hasMustache: z.boolean().optional().default(false),
  usesReadingGlasses: z.boolean().optional().default(false),
  accessoryName: z.string().trim().max(120).optional(),
  // Optional reference photo (#34): a `data:image/jpeg;base64,…` dataURL produced
  // by the client downscaler (downscaleToDataUrl, ~640px longest side). The worker
  // decodes it, stores the bytes in R2, and keeps only the R2 key on the row. Cap
  // the string so a bad/huge upload can't bloat the JSON body — a 640px JPEG is
  // well under this; base64 is ~1.34× the byte size.
  photo: z.string().max(2_000_000).optional(),
});
export type KidsPublicIntakeChild = z.infer<typeof KidsPublicIntakeChildSchema>;

// POST /api/kids/intake (public) — consume a token → parent + checked_in meeting.
export const KidsIntakeReqSchema = z.object({
  token: z.string().trim().min(1).max(120),
  parent: z.object({
    name: z.string().trim().min(1).max(200),
    phone: z.string().trim().max(30).optional(), // optional (Tim 08.07)
    email: z.string().trim().regex(EMAIL_RE).max(200), // REQUIRED (Tim 08.07 — welcome email)
  }),
  children: z.array(KidsPublicIntakeChildSchema).min(1).max(20),
  dataConsent: z.literal(true), // parental consent offer — required to submit (Tim 08.07)
});
export type KidsIntakeReq = z.infer<typeof KidsIntakeReqSchema>;

export const KidsIntakeResSchema = z.object({ ok: z.literal(true), meetingId: z.string() });
export type KidsIntakeRes = z.infer<typeof KidsIntakeResSchema>;

// GET /api/kids/intake/:token (public) — validate + show the office on the form.
// docs = the linked moderator's consent PDFs (ICF checkbox); consentText = the
// resolved parental-consent offer text (moderator→office→default) so the parent
// form shows the SAME two consents as the Remote landing (Tim 09.07).
export const KidsIntakePreviewDocSchema = z.object({
  id: z.string(),
  filename: z.string(),
  url: z.string(),
});
export const KidsIntakePreviewResSchema = z.object({
  ok: z.literal(true),
  office: z.string(),
  officeName: z.string().nullable(),
  docs: z.array(KidsIntakePreviewDocSchema).default([]),
  consentText: z.string().default(''),
});
export type KidsIntakePreviewRes = z.infer<typeof KidsIntakePreviewResSchema>;

// POST /api/kids/admin/intake-link (admin|manager) — mint an intake link.
export const KidsIntakeLinkCreateReqSchema = z.object({
  office: z.string().trim().max(60).nullable().optional(), // manager picks; admin forced to own
  moderator: z.string().trim().max(80).optional(), // optional attribution
  label: z.string().trim().max(120).optional(),
});
export type KidsIntakeLinkCreateReq = z.infer<typeof KidsIntakeLinkCreateReqSchema>;

export const KidsIntakeLinkCreateResSchema = z.object({
  token: z.string(),
  url: z.string(),
  office: z.string(),
  moderator: z.string().nullable(),
});
export type KidsIntakeLinkCreateRes = z.infer<typeof KidsIntakeLinkCreateResSchema>;

// POST /api/kids/mod/intake-link (moderator, self-scoped) — the caller mints
// their OWN parent-form link (Tim 09.07, #67). Unlike the admin endpoint, office
// + moderator are FORCED from the authenticated session (never client input) —
// the body carries at most an optional label. The moderator gets a direct link to
// the parent form (#/intake?token=…), attributed to themselves.
export const KidsModIntakeLinkCreateReqSchema = z.object({
  label: z.string().trim().max(120).optional(),
});
export type KidsModIntakeLinkCreateReq = z.infer<typeof KidsModIntakeLinkCreateReqSchema>;

// Same Res shape as the admin mint (token + url + office + moderator).
export const KidsModIntakeLinkCreateResSchema = KidsIntakeLinkCreateResSchema;
export type KidsModIntakeLinkCreateRes = z.infer<typeof KidsModIntakeLinkCreateResSchema>;
